import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2, Search, AlertTriangle, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/components/ui/use-toast';
import GymMemberFormDialog from '../components/GymMemberFormDialog';
import MembershipSummary from '../components/MembershipSummary';
import { gymMembersService } from '../../services/gymMembers';
import { errorCodeOf } from '../../services/api-error';
import type { GymMember } from '../../types/gym-member';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import { useGymAdmin } from '../context/gym-admin-context';

export default function MembersPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { placePath } = useGymAdmin();
  // Not `useCurrentGym`: this roster also serves a trophy, and that hook goes
  // through the Gym-only endpoint, which 404s on one.
  const { location: place, loading: gymLoading, slug } = useCurrentLocation();

  const [members, setMembers] = useState<GymMember[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dpaRequired, setDpaRequired] = useState(false);

  const loadFirstPage = useCallback(
    async (gymSlug: string, term: string) => {
      setListLoading(true);
      try {
        const result = await gymMembersService.list(gymSlug, { search: term || undefined });
        setMembers(result.members);
        setTotal(result.total);
        setHasMore(result.hasMore);
        setPage(1);
      } catch (error) {
        // Not signing the Art. 28 agreement is not a failure to load: the
        // server is refusing to serve a roster it has no contract to hold, and
        // the admin needs the way out rather than a red toast.
        if (errorCodeOf(error) === 'dpa_required') {
          setDpaRequired(true);
          return;
        }
        console.error('Failed to load members:', error);
        toast({ title: t('gymAdmin.members.loadError'), variant: 'destructive' });
      } finally {
        setListLoading(false);
      }
    },
    [t, toast]
  );

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    if (!slug) return;

    const timer = setTimeout(() => loadFirstPage(slug, search), 300);
    return () => clearTimeout(timer);
  }, [slug, search, loadFirstPage]);

  const loadMore = async () => {
    if (!slug || loadingMore) return;

    setLoadingMore(true);
    try {
      const next = page + 1;
      const result = await gymMembersService.list(slug, {
        search: search || undefined,
        page: next,
      });
      setMembers((prev) => [...prev, ...result.members]);
      setTotal(result.total);
      setHasMore(result.hasMore);
      setPage(next);
    } catch (error) {
      console.error('Failed to load more members:', error);
      toast({ title: t('gymAdmin.members.loadError'), variant: 'destructive' });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSaved = (member: GymMember) => {
    setDialogOpen(false);
    if (slug) loadFirstPage(slug, search);

    // The backend attaches a matching account on save, so say so rather than
    // leaving the new badge to be noticed.
    if (member.linked) {
      toast({
        title: t('gymAdmin.members.linkedTitle'),
        description: t('gymAdmin.members.linkedDescription'),
      });
    }
  };

  const handleDelete = async (member: GymMember) => {
    if (!slug) return;
    const confirmed = window.confirm(
      t('gymAdmin.members.confirmDelete', { name: member.full_name })
    );
    if (!confirmed) return;

    try {
      await gymMembersService.remove(slug, member.id);
      setMembers((prev) => prev.filter((m) => m.id !== member.id));
      setTotal((prev) => Math.max(prev - 1, 0));
      toast({ title: t('gymAdmin.members.deleted') });
    } catch (error) {
      toast({
        title: t('gymAdmin.members.deleteError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    }
  };

  if (gymLoading) return <Skeleton className="h-96 w-full max-w-5xl" />;
  if (!place || !slug) return null;

  // The server will not serve this roster until the gym has signed, so there is
  // nothing to show here and no point offering the "add member" button either.
  if (dpaRequired) {
    return (
      <Card className="max-w-2xl border-amber-500/50">
        <CardHeader className="flex flex-row items-start gap-3 space-y-0">
          <ShieldAlert className="mt-0.5 size-5 shrink-0 text-amber-500" />
          <div className="space-y-1">
            <CardTitle className="text-base">{t('gymAdmin.members.dpaBlockedTitle')}</CardTitle>
            <CardDescription>{t('gymAdmin.members.dpaBlockedDescription')}</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <Button onClick={() => navigate(placePath('/dpa'))}>
            {t('gymAdmin.members.dpaBlockedAction')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{t('gymAdmin.members.subtitle')}</p>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="mr-2 size-4" />
          {t('gymAdmin.members.new')}
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder={t('gymAdmin.members.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('gymAdmin.members.searchPlaceholder')}
        />
      </div>

      {listLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : members.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {search ? t('gymAdmin.members.noResults') : t('gymAdmin.members.empty')}
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {t('gymAdmin.members.countShown', { shown: members.length, total })}
          </p>

          <div className="space-y-3">
            {members.map((member) => (
              <Card key={member.id}>
                <CardContent className="flex flex-wrap items-start gap-4 p-4">
                  {/* Only linked members have an account to show a face for. */}
                  {member.linked && (
                    <Avatar className="size-10 shrink-0">
                      <AvatarImage src={member.user_avatar_small_url ?? undefined} alt="" />
                      <AvatarFallback>{member.name.charAt(0).toUpperCase()}</AvatarFallback>
                    </Avatar>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate font-medium">{member.full_name}</h3>
                      {member.linked && <Badge>{t("gymAdmin.members.linked")}</Badge>}
                      {member.invited && (
                        <Badge variant="secondary" title={t("gymAdmin.members.invitedHint")}>
                          {t("gymAdmin.members.invited")}
                        </Badge>
                      )}
                      {member.status === 'inactive' && (
                        <Badge variant="outline">{t('gymAdmin.members.status.inactive')}</Badge>
                      )}
                      {/* Under the consent age: no account was invited for
                          this row, whatever address is on it. */}
                      {member.minor && (
                        <Badge variant="outline" title={t('gymAdmin.members.minorHint')}>
                          {t('gymAdmin.members.minor')}
                        </Badge>
                      )}
                      {member.medical_certificate_expired && (
                        <Badge variant="destructive" className="gap-1">
                          <AlertTriangle className="size-3" />
                          {t('gymAdmin.members.certificateExpired')}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-sm text-muted-foreground">
                      {member.email && <span className="truncate">{member.email}</span>}
                      {member.phone && <span>{member.phone}</span>}
                    </div>

                    <MembershipSummary
                      cards={member.membership_cards}
                      expiresOn={member.membership_expires_on}
                      pendingCount={member.membership_cards_pending}
                    />
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    {/* Its own URL, so an admin can share or reopen the record. */}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate(placePath(`/members/${member.id}/edit`))}
                      aria-label={t('common.edit')}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => handleDelete(member)}
                      aria-label={t('common.delete')}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={loadMore} disabled={loadingMore}>
                {loadingMore && <Loader2 className="mr-2 size-4 animate-spin" />}
                {t('gymAdmin.members.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Creation only — editing lives on its own page. */}
      <GymMemberFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        gymSlug={slug}
        member={null}
        onSaved={handleSaved}
      />
    </div>
  );
}
